export const STEPS = [
  { num: 1, title: 'Upload Content', subtitle: 'For Experience' },
  { num: 2, title: 'Review and Edit', subtitle: '' },
  { num: 3, title: 'Team Setup', subtitle: '' },
  { num: 4, title: 'Select Assets', subtitle: '' },
  { num: 5, title: 'Generate Story', subtitle: '' },
];

export const INITIAL_BOT_CONFIGS = [
  {
    id: 'lxd',
    title: 'LXD',
    model: 'gpt-4o',
    description: `## **Learning Experience Design (LXD) Consultant**

## **Role and Purpose**
You are a **Learning Experience Design Consultant** responsible for transforming raw training content into structured learning premises for the **VR Training Researcher Agent**. Your expertise in learning theory...`,
  },
  {
    id: 'researcher',
    title: 'Researcher',
    model: 'gpt-4o',
    description: `## Researcher Agent for VR Experience Design

### ROLE
YOU are the **VR Experience Researcher Agent**. Your job is to transform raw user content (e.g., SOPs, briefings, outlines) into a **structured, sequenced experience design** broken into clear chapters.`,
  },
  {
    id: 'concept-designer',
    title: 'Concept Designer',
    model: 'gpt-4o',
    description: `## Concept Designer Agent for VR Experience Design

### ROLE
YOU are the **VR Concept Designer Agent**. You will convert a single chapter of a VR experience into a precise, moment-by-moment design. Your output is used directly by the next bot to script VR interactions.`,
  },
  {
    id: 'moment-writer',
    title: 'Moment Writer',
    model: 'gpt-4o',
    description: `## Moment Writer Agent for VR Experience Design

### ROLE
YOU are the **Moment Writer Agent** in a prompt-to-VR system. You receive a moment design for a whole chapter from the Concept Designer and your job is to generate the **complete interaction block** (called a Moment block) for 1 SPECIFIC MOMENT.`,
  },
];

const createMomentMode = ({
  overview = {},
  general = {},
  interaction = {},
  beforeTouch = {},
  afterTouch = {},
  voiceWarnings = [],
  notes = '',
}) => ({
  overview: {
    touchObjectQuery: '',
    voiceOver: '',
    objects: '',
    displayText: '',
    metaLayer: '',
    beforeTouch: {},
    afterTouch: {},
    ...overview,
  },
  general: {
    name: '',
    description: '',
    ...general,
  },
  interaction: {
    touchObjectQuery: '',
    interactionType: 'poke', // Default interaction type
    duration: 0,
    required: true,
    ...interaction,
  },
  beforeTouch: {
    objects: {
      query: '',
      option: '',
      spawn: '',
      ...(beforeTouch.objects || {}),
    },
    voiceOver: beforeTouch.voiceOver || '',
    displayText: beforeTouch.displayText || '',
    image: beforeTouch.image || '',
    video: beforeTouch.video || '',
  },
  afterTouch: {
    sfx: afterTouch.sfx || '',
    objects: {
      query: '',
      option: '',
      spawn: '',
      ...(afterTouch.objects || {}),
    },
    voiceOver: afterTouch.voiceOver || '',
    displayText: afterTouch.displayText || '',
    image: afterTouch.image || '',
    video: afterTouch.video || '',
    metaLayer: afterTouch.metaLayer || '',
  },
  voiceWarnings,
  notes,
});

const createMoment = ({ id, nonTechnical, technical }) => ({
  id,
  content: {
    nonTechnical: createMomentMode(nonTechnical),
    technical: createMomentMode(technical),
  },
});

export const INITIAL_CHAPTERS = [
  {
    id: 'chapter-1',
    number: 1,
    title: 'Orientation & Navigation',
    moments: [
      createMoment({
        id: 'moment-1-1',
        nonTechnical: {
          overview: {
            touchObjectQuery: 'directionalbuttons',
            voiceOver:
              'To begin exploring, press the forward directional button in front of you.',
            objects: 'Forward button mesh',
            displayText: 'Press Forward to Move',
            metaLayer: 'navigation_hints',
            beforeTouch: {
              metaLayer: 'button_idle',
              description: 'Button glows softly, anchor arrow points forward.',
            },
            afterTouch: {
              sfx: 'forward_confirm_soft.wav',
              metaLayer: 'button_pressed',
              description: 'Confirm tone plays and mini world slides forward.',
            },
          },
          general: {
            name: 'Press Forward Directional Button',
            description:
              'The learner pushes the glowing forward pad to advance the miniature environment.',
          },
          interaction: {
            touchObjectQuery: 'pressforwardbutton',
          },
          beforeTouch: {
            objects: {
              query: 'forward_pad_mesh',
              option: 'Highlight',
              spawn: 'Always visible',
            },
            voiceOver:
              'Gently press the glowing forward arrow to slide the scene toward you.',
            displayText: 'Press Forward',
            image: 'ui/navigation_forward.png',
            video: '',
          },
          afterTouch: {
            sfx: 'forward_confirm.wav',
            objects: {
              query: 'forward_pad_mesh',
              option: 'Animate press',
              spawn: 'Remains active',
            },
            voiceOver: 'Great! You have advanced the miniature environment.',
            displayText: 'Movement Confirmed',
            image: '',
            video: '',
            metaLayer: 'navigation_feedback',
          },
          voiceWarnings: [
            {
              id: 'warning-1',
              touchObjectQuery: 'pressforwardbutton',
              message: 'Apply steady pressure—tapping may not register.',
            },
          ],
          notes:
            'This is the very first interaction. Keep guidance encouraging.',
        },
        technical: {
          overview: {
            touchObjectQuery: 'nav.forward_pad',
            voiceOver: 'System voice event: NAV_FORWARD_GUIDE',
            objects: 'ObjectRef.nav_forward_pad',
            displayText: 'HUD Label: NAV_FORWARD',
            metaLayer: 'meta.navigation.forward',
            beforeTouch: {
              metaLayer: 'meta.navigation.forward.idle',
              description: 'Shader: glow_idle, Animation: hover_idle',
            },
            afterTouch: {
              sfx: 'sfx.nav.forward.confirm',
              metaLayer: 'meta.navigation.forward.confirmed',
              description: 'Shader: glow_press, Animation: press_confirm',
            },
          },
          general: {
            name: '[TECH] Press Forward Directional Button',
            description:
              'Trigger nav.forward_pad interaction; translate miniature root along +Z.',
          },
          interaction: {
            touchObjectQuery: 'nav.forward_pad.trigger_zone',
          },
          beforeTouch: {
            objects: {
              query: 'FX_Highlight/Nav_Forward',
              option: 'GlowPulse',
              spawn: 'OnSceneLoad',
            },
            voiceOver: 'VO_NAV_FORWARD_GUIDE',
            displayText: 'UIString.NAV_FORWARD_PROMPT',
            image: 'TextureRefs/ui/nav_forward_prompt',
            video: '',
          },
          afterTouch: {
            sfx: 'AudioCue.NAV_FORWARD_CONFIRM',
            objects: {
              query: 'FX_Click/Nav_Forward',
              option: 'Pulse',
              spawn: 'OnActivation',
            },
            voiceOver: 'VO_NAV_FORWARD_SUCCESS',
            displayText: 'UIString.NAV_COMPLETE',
            image: '',
            video: 'VideoRefs/tutorial/nav_forward_recap',
            metaLayer: 'meta.nav.forward.complete',
          },
          voiceWarnings: [
            {
              id: 'tech-warning-1',
              touchObjectQuery: 'nav.forward_pad.trigger_zone',
              message:
                'Check collider alignment if trigger fails. Ensure InputManager.navForward enabled.',
            },
          ],
          notes:
            'Ensure InputRouter registers press on nav.forward_pad in VR and desktop modes.',
        },
      }),
      createMoment({
        id: 'moment-1-2',
        nonTechnical: {
          general: {
            name: 'Rotate View with Controls',
            description:
              'Learner rotates the scene using the dial around the base.',
          },
          interaction: {
            touchObjectQuery: 'rotateDial',
          },
          beforeTouch: {
            voiceOver:
              'Grip the round dial and twist to rotate the miniature world.',
          },
          afterTouch: {
            sfx: 'rotate_confirm.wav',
          },
        },
        technical: {
          general: {
            name: '[TECH] Rotate View with Controls',
            description: 'Dial rotation drives root rotation on Y axis.',
          },
          interaction: {
            touchObjectQuery: 'controls.rotate_dial.collider',
          },
          afterTouch: {
            sfx: 'AudioCue.ROTATE_CONFIRM',
          },
        },
      }),
    ],
  },
  {
    id: 'chapter-2',
    number: 2,
    title: 'Spatial Awareness',
    moments: [
      createMoment({
        id: 'moment-2-1',
        nonTechnical: {
          general: {
            name: 'Zoom into Specific Area',
            description: 'Learner pinches to zoom onto highlighted zones.',
          },
          interaction: {
            touchObjectQuery: 'pinchZoomArea',
          },
          beforeTouch: {
            voiceOver: 'Pinch outward to zoom closer to the marked console.',
          },
        },
        technical: {
          general: {
            name: '[TECH] Zoom into Specific Area',
            description:
              'Pinch gesture scales scene segment (zone_02) by 1.5x.',
          },
          interaction: {
            touchObjectQuery: 'gesture.pinch.zone_02',
          },
        },
      }),
    ],
  },
];

export const SUPPORTED_FILE_FORMATS = 'PDF, DOCX, PPTX, TXT';
export const MAX_FILE_SIZE = 25; // MB
export const ASSET_FILE_TYPES = '.glb,.gltf,.fbx,.obj,.dae,.3ds,.blend';
